import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Loader2 } from "lucide-react";
import "./ExpWithHumansEditPage.css";
import progressIcon from "../../assets/progressIcon6.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import dogLevel0 from "../../assets/dogLevel0.png";
import dogLevel1 from "../../assets/dogLevel1.png";
import dogLevel2 from "../../assets/dogLevel2.png";
import dogLevel3 from "../../assets/dogLevel3.png";
import dogLevel4 from "../../assets/dogLevel4.png";
import appIcon from "../../assets/appIcon.svg";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";
import apiClient from "../../api/apiClient";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";

const ExpWithHumansEditPage = () => {
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.user);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const sliderRef = useRef(null);
  const isDraggingRef = useRef(false);

  const experienceLevels = useMemo(
    () => [
      { value: 0, label: "not friendly with any other humans" },
      { value: 1, label: "not friendly with most humans" },
      { value: 2, label: "50/50 friendly with humans" },
      { value: 3, label: "friendly with most humans" },
      { value: 4, label: "friendly with all humans" },
    ],
    [],
  );

  const [formData, setFormData] = useState({
    dogName: "",
    socialExperienceWithHumans: 1,
  });

  const [sliderValue, setSliderValue] = useState(1);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          const userData = res.data.data;
          setUser(userData);

          if (userData.dogs && userData.dogs.length > 0) {
            const dog = userData.dogs[0];

            const initialLevel = experienceLevels.findIndex(
              (level) =>
                level.label.toLowerCase() ===
                dog.along_with_other_humans?.toLowerCase(),
            );

            const finalValue = initialLevel !== -1 ? initialLevel : 1;

            setFormData({
              dogName: dog.dog_name || "Dog",
              socialExperienceWithHumans: finalValue,
            });
            setSliderValue(finalValue);
          }
        }
      } catch (err) {
        console.error("Error fetching details:", err);
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id) fetchDetails();
  }, [profile?.id, experienceLevels]);

  const dogName = formData.dogName || "Dog";
  const TRACK_START = 5;
  const TRACK_END = 95;
  const TRACK_RANGE = TRACK_END - TRACK_START;
  const SLIDER_STEPS = 4;

  const handleSliderChange = (value) => {
    const clamped = Math.max(0, Math.min(SLIDER_STEPS, value));
    setSliderValue(clamped);
  };

  const handleNext = async () => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      const dogId = user?.dogs?.[0]?.id;
      if (!dogId) return;

      const apiPayload = {
        user_id: profile?.id,
        along_with_other_humans: experienceLevels[sliderValue].label,
      };

      const response = await apiClient.put(
        `/dogs/update-dog-by-id/${dogId}`,
        apiPayload,
      );

      if (response.data?.status) {
        navigate("/edit-dog-bio");
      }
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSkip = () => {
    setSliderValue(formData.socialExperienceWithHumans);
    navigate("/edit-dog-bio");
  };

  const handleExit = () => {
    setSliderValue(formData.socialExperienceWithHumans);
    navigate("/user-profile");
  };

  const dogLevelImages = [
    dogLevel0,
    dogLevel1,
    dogLevel2,
    dogLevel3,
    dogLevel4,
  ];
  const currentDogHead = dogLevelImages[sliderValue] || dogLevel0;

  const updateValueFromPosition = (clientX, clientY) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 1023;

    let percent;

    if (isMobile) {
      let y = clientY - rect.top;
      y = Math.max(0, Math.min(rect.height, y));
      percent = y / rect.height;
    } else {
      let x = clientX - rect.left;
      x = Math.max(0, Math.min(rect.width, x));
      percent = x / rect.width;
    }

    const steppedValue = Math.round(percent * SLIDER_STEPS);
    handleSliderChange(steppedValue);
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    updateValueFromPosition(clientX, clientY);
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", stopDragging);
    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", stopDragging);
    };
  }, []);

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  const valPosPercent = `${TRACK_START + (sliderValue / SLIDER_STEPS) * TRACK_RANGE}%`;

  return (
    <div className="ewhe-container">
      <MobileTopBar
        onBack={() => navigate("/edit-exp-with-dogs")}
        backColor="#fff"
        logoColor="#fff"
      />

      {/* Top Progress Section (Desktop Only) */}
      <div className="paws-exp-header-d paws-desktop-only">
        <div className="top-progress-wrapper">
          {/* <button
            className="login-back-btn"
            onClick={() => navigate("/edit-exp-with-dogs")}
          >
            <ArrowLeft size={24} color="#fff" strokeWidth={3} />
            <span style={{ color: "#fff" }}>Back</span>
          </button> */}
          <DesktopTopBar
            showBack={true}
            onBack={() => navigate("/edit-exp-with-dogs")}
            backColor="#fff"
          />
          <div className="dog-icon-top">
            <img
              src={progressIcon}
              alt="Progress"
              className="paws-progress-icon-img"
              style={{ marginRight: "0" }}
            />
          </div>
          <div
            className="main-progress-bar"
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "white",
            }}
          >
            <div className="main-progress-fill" style={{ width: "100%" }}></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ewhe-content">
        {/* Mobile Progress Bar (Hidden on Desktop) */}
        <div className="ewhe-mobile-progress-wrapper ewhe-mobile-only">
          <div className="ewhe-dog-icon-top">
            <img
              src={progressIcon}
              alt="Progress"
              className="ewhe-progress-icon-img"
            />
          </div>
          <div
            className="ewhe-main-progress-bar"
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "white",
            }}
          >
            <div
              className="ewhe-main-progress-fill"
              style={{ width: "100%" }}
            ></div>
          </div>
        </div>

        <h1 className="ewhe-title">
          HOW DOES {dogName.toUpperCase()} GET ALONG WITH OTHER HUMANS?
        </h1>
        <p className="ewhe-subtitle">Honesty is the best Pawlicy!</p>

        {/* Custom Slider Component */}
        <div
          className="ewhe-slider-container"
          ref={sliderRef}
          onClick={handleTrackClick}
          style={{ "--pos": valPosPercent }}
        >
          {/* Track Background */}
          <div className="ewhe-slider-track-bg"></div>

          {/* Filled Track */}
          <div className="ewhe-slider-track-fill"></div>

          {/* Dots and Labels */}
          <div className="ewhe-slider-steps-wrapper">
            {[...Array(SLIDER_STEPS + 1)].map((_, index) => {
              const stepPosPercent = `${TRACK_START + (index / SLIDER_STEPS) * TRACK_RANGE}%`;
              return (
                <div
                  key={index}
                  className={`ewhe-slider-step-item ${index % 2 === 0 ? "left-label" : "right-label"}`}
                  style={{ "--step-pos": stepPosPercent }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSliderChange(index);
                  }}
                >
                  <div className="ewhe-slider-dot"></div>
                  <span
                    className={`ewhe-slider-label ${index === sliderValue ? "ewhe-active-label" : ""}`}
                  >
                    {experienceLevels[index]?.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Dog Head Thumb */}
          <div
            className="ewhe-slider-thumb-dog"
            style={{
              left:
                window.innerWidth <= 1023
                  ? sliderValue <= 2
                    ? "50%"
                    : "49%"
                  : undefined,
            }}
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
          >
            <img
              src={currentDogHead}
              alt="DogIcon"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              className={`ewhe-dog-level-icon ${sliderValue >= 3 ? "ewhe-dog-level-large" : ""}`}
            />
          </div>
        </div>
      </div>

      <div className="ewhe-footer desktop-only">
        {/* Desktop Only Exit */}
        <button
          className="ewhe-skip-btn ewhe-desktop-only"
          onClick={handleExit}
        >
          EXIT
        </button>

        <button onClick={handleSkip} className="ewhe-skip-btn">
          SKIP
        </button>
        <button
          onClick={handleNext}
          className="ewhe-next-btn"
          disabled={isUpdating}
        >
          <span className="ewhe-next-text">
            {isUpdating ? "UPDATING..." : "NEXT"}
          </span>
          <div className={"ewhe-next-circle"}>
            {isUpdating ? (
              <Loader2
                className="animate-spin"
                color="rgba(182, 252, 238, 1)"
                size={20}
              />
            ) : (
              <img
                src={nextIcon}
                alt="Next"
                style={{ width: "140%", height: "140%", objectFit: "contain" }}
              />
            )}
          </div>
        </button>
      </div>
      <div className="ewhe-footer mobile-only">
        <MobileBottomBar
          onNext={handleNext}
          isNextEnabled={true}
          nextIconSrc={nextIcon}
          showSkip={true}
          skipText="EXIT"
          onSkip={handleExit}
          variant="green"
        />
      </div>
    </div>
  );
};

export default ExpWithHumansEditPage;
